import { CreditCard, Building2, Handshake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import CheckoutDisclaimer from "./CheckoutDisclaimer";
import { useEffect } from "react";
import { analytics } from "@/lib/analytics";

interface PaymentOption {
  id: string;
  title: string;
  subtitle: string;
  icon: React.ComponentType<{ className?: string }>;
  planLength: string;
  monthlyAmount: string;
  fees: string;
  badge: string;
  badgeVariant: "default" | "secondary" | "outline";
}

interface OfferComparisonDrawerProps {
  amount?: number;
  isOpen?: boolean;
  onClose?: () => void;
  onSelectOption?: (optionId: string) => void;
}

const OfferComparisonDrawer = ({ 
  amount = 30000, 
  isOpen = true, 
  onClose, 
  onSelectOption 
}: OfferComparisonDrawerProps) => {
  // Track compare_open event when drawer opens
  useEffect(() => {
    if (isOpen) {
      analytics.track('compare_open', {
        metadata: { amount }
      });
    }
  }, [isOpen, amount]);

  // Calculate estimated payments based on eligibility and amount
  const bnplEligible = amount <= 20000;
  const bankEligible = amount >= 20000 && amount <= 100000;
  const extendedEligible = amount >= 50000;
  
  // BNPL: 3-4 payments
  const bnpl3Payments = Math.round(amount / 3);
  const bnpl4Payments = Math.round(amount / 4);
  
  // Bank EMI: 12 and 24 month options
  const bank12Monthly = Math.round(amount / 12);
  const bank24Monthly = Math.round(amount / 24);
  
  // Extended Finance: Custom terms (36 months example)
  const extended36Monthly = Math.round(amount / 36);

  const paymentOptions: PaymentOption[] = [
    {
      id: "bnpl",
      title: "BNPL",
      subtitle: bnplEligible ? "3–4 payments" : "Not eligible",
      icon: CreditCard,
      planLength: bnplEligible ? "3–4 payments" : "Amount too high",
      monthlyAmount: bnplEligible 
        ? `AED ${bnpl3Payments.toLocaleString()} - ${bnpl4Payments.toLocaleString()}/payment`
        : "N/A",
      fees: bnplEligible ? "0% if eligible" : "Max AED 10-20K",
      badge: "Fastest approval",
      badgeVariant: bnplEligible ? "default" : "outline",
    },
    {
      id: "bank",
      title: "Bank Installments", 
      subtitle: bankEligible ? "6–24 months" : "Not eligible",
      icon: Building2,
      planLength: bankEligible ? "12 or 24 months" : "Amount out of range",
      monthlyAmount: bankEligible 
        ? `AED ${bank24Monthly.toLocaleString()}/mo (24m) - ${bank12Monthly.toLocaleString()}/mo (12m)`
        : "N/A",
      fees: bankEligible ? "Low APR" : "AED 20K-100K range",
      badge: "Most trusted",
      badgeVariant: bankEligible ? "secondary" : "outline",
    },
    {
      id: "extended",
      title: "Extended Finance",
      subtitle: extendedEligible ? "Custom terms" : "Not eligible",
      icon: Handshake,
      planLength: extendedEligible ? "Custom terms" : "Min AED 50,000",
      monthlyAmount: extendedEligible 
        ? `AED ${extended36Monthly.toLocaleString()}/mo (36m example)`
        : "N/A",
      fees: extendedEligible ? "Competitive rates" : "Min AED 50,000",
      badge: "Best for large milestones",
      badgeVariant: extendedEligible ? "outline" : "outline",
    },
  ];

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:max-w-4xl">
        <SheetHeader>
          <SheetTitle className="text-2xl font-heading font-bold">Your Payment Options</SheetTitle>
          <SheetDescription className="font-body">
            Choose the payment plan that works best for you
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {paymentOptions.map((option) => (
              <div 
                key={option.id} 
                className="bg-card rounded-xl border p-4 hover:shadow-md smooth-transition"
              >
                {/* Header with Badge */}
                <div className="text-center mb-4">
                  <Badge 
                    variant={option.badgeVariant} 
                    className="mb-3 text-xs"
                  >
                    {option.badge}
                  </Badge>
                  <div className="flex items-center justify-center w-12 h-12 gradient-bg rounded-lg mx-auto mb-3">
                    <option.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-heading font-bold text-lg mb-1">
                    {option.title}
                  </h3>
                  <p className="text-muted-foreground text-sm font-body">
                    {option.subtitle}
                  </p>
                </div>

                {/* Payment Details */}
                <div className="space-y-3 mb-4">
                  <div className="text-center p-3 bg-secondary/30 rounded-lg">
                    <p className="font-bold text-xl font-heading text-primary mb-1">
                      {option.monthlyAmount}
                    </p>
                    <p className="text-xs text-muted-foreground font-body">
                      {option.fees}
                    </p>
                  </div>
                </div>

                {/* CTA Button */}
                <Button 
                  className="w-full" 
                  variant="outline"
                  onClick={() => onSelectOption?.(option.id)}
                >
                  Select {option.title}
                </Button>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="mt-6 text-center">
            <p className="text-xs text-muted-foreground font-body mb-2">
              Illustrative only — final terms depend on partners.
            </p>
            <p className="text-xs text-muted-foreground font-body">
              Eligibility subject to partner approval & final checks.
            </p>
          </div>
          
          <CheckoutDisclaimer />
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default OfferComparisonDrawer;